import { createWorker, OEM, PSM } from 'tesseract.js';

const normalizeSpaces = (value = '') => String(value).replace(/\s+/g, ' ').trim();

const parseCOP = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  const number = Number(digits);
  return Number.isFinite(number) ? number : 0;
};

const parseDate = (text) => {
  const match = String(text).match(/\b([0-3]?\d)[\s/.-]+([01]?\d)[\s/.-]+(20\d{2}|\d{2})\b/);
  if (!match) return '';
  const year = match[3].length === 2 ? `20${match[3]}` : match[3];
  return `${year}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
};

const compactText = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '');

const ignoredLine = (line) => {
  const compact = compactText(line);
  return /^(total|subtotal|efectivo|abono|saldo|cambio|iva|impuesto|impoconsumo|descuento|retefuente|reteiva|reteica|retencion|flete|propina|basegravable|baseiva|redondeo)/.test(compact)
    || /^(pedidodeventa|ordendecompra|remision)/.test(compact)
    || /\b(total|subtotal|efectivo|abono|saldo|cambio|iva|impuesto|impoconsumo|descuento|retefuente|reteiva|reteica|retenci[oó]n|flete|propina|fecha|factura|nit|tel[eé]fono|direcci[oó]n|se[nñ]or)\b/i.test(line);
};

const MAX_AUTOMATIC_QUANTITY = 1000;

const buildReviewItem = ({ description, quantity, unitCost, totalCost, confidence, sourceLine, reason }) => ({
  description,
  quantity,
  unit: 'unidad',
  totalCost,
  unitCost,
  confidence: Math.min(Number(confidence || 0) / 100, 0.85),
  needsReview: true,
  reviewReason: reason,
  ...(sourceLine ? { sourceLine } : {}),
});

// Las facturas impresas suelen traer precio unitario y total. Esa relación permite
// recuperar la cantidad aunque el OCR omita su columna o la confunda con el ítem.
const parsePrintedTableLine = (line, index, confidence, { hasUnitCostColumn = false } = {}) => {
  const clean = normalizeSpaces(line);
  if (!clean || ignoredLine(clean)) return null;

  const currencyMatches = [...clean.matchAll(/\$\s*\d[\d.,]*/g)];
  const moneyMatches = currencyMatches.length >= 2
    ? currencyMatches
    : [...clean.matchAll(/\d+(?:[.,]\d+)*/g)];
  if (moneyMatches.length < 2) return null;

  // Sin encabezado de precio unitario, dos números sin símbolo monetario son
  // ambiguos: normalmente representan cantidad + total. Se procesan abajo
  // sin inferir miles de unidades a partir de un supuesto costo de $2 o $10.
  if (currencyMatches.length < 2 && moneyMatches.length === 2 && !hasUnitCostColumn) return null;

  const unitCost = parseCOP(moneyMatches[moneyMatches.length - 2][0]);
  const totalCost = parseCOP(moneyMatches[moneyMatches.length - 1][0]);
  if (unitCost <= 0 || totalCost < 100) return null;

  const inferredQuantity = totalCost / unitCost;
  const roundedQuantity = Math.round(inferredQuantity);
  if (!Number.isFinite(inferredQuantity)
    || roundedQuantity <= 0
    || roundedQuantity > MAX_AUTOMATIC_QUANTITY
    || Math.abs(inferredQuantity - roundedQuantity) > 0.02) return null;

  let description = clean.slice(0, moneyMatches[moneyMatches.length - 2].index).trim();
  description = description.replace(/^\s*\d{1,4}\s+/, '');

  const trailingQuantity = description.match(/(?:^|\s)(\d+(?:[.,]\d+)?)\s*$/);
  if (trailingQuantity) {
    const readQuantity = Number(trailingQuantity[1].replace(',', '.'));
    if (Math.abs(readQuantity - roundedQuantity) < 0.02) {
      description = description.slice(0, trailingQuantity.index).trim();
    }
  }

  description = description.replace(/^[-|:;.,]+|[-|:;.,]+$/g, '').trim();
  if (!description || description.length < 2 || !/[a-záéíóúñ]/i.test(description)) return null;

  return buildReviewItem({
    description,
    quantity: roundedQuantity,
    unitCost,
    totalCost,
    confidence,
    sourceLine: index + 1,
    reason: 'Fila impresa reconstruida con precio unitario y total: confirma el nombre.',
  });
};

const parseItemLine = (line, index, confidence, options = {}) => {
  const clean = normalizeSpaces(line);
  if (!clean || ignoredLine(clean)) return null;

  const printedItem = parsePrintedTableLine(clean, index, confidence, options);
  if (printedItem) return printedItem;

  const amountMatch = clean.match(/(?:\$\s*)?(\d[\d.,]{2,})\s*$/);
  if (!amountMatch) return null;
  const totalCost = parseCOP(amountMatch[1]);
  if (totalCost < 100) return null;

  const beforeAmount = clean.slice(0, amountMatch.index).trim();
  const leadingQuantity = beforeAmount.match(/^\s*(\d+(?:[.,]\d+)?)\s+(.+)$/);
  const trailingQuantity = beforeAmount.match(/^(.+?[a-záéíóúñ])\s+(\d+(?:[.,]\d+)?)\s*$/i);
  const leadingValue = leadingQuantity ? Number(leadingQuantity[1].replace(',', '.')) : 0;
  const trailingValue = trailingQuantity ? Number(trailingQuantity[2].replace(',', '.')) : 0;
  const validLeading = Number.isFinite(leadingValue) && leadingValue > 0 && leadingValue <= MAX_AUTOMATIC_QUANTITY;
  const validTrailing = Number.isFinite(trailingValue) && trailingValue > 0 && trailingValue <= MAX_AUTOMATIC_QUANTITY;
  const quantity = validLeading ? leadingValue : (validTrailing ? trailingValue : 1);
  const rawDescription = validLeading
    ? leadingQuantity[2]
    : (validTrailing ? trailingQuantity[1] : beforeAmount);
  const description = normalizeSpaces(rawDescription)
    .replace(/^[-|:;.,]+|[-|:;.,]+$/g, '')
    .trim();
  if (!description || description.length < 2 || !/[a-záéíóúñ]/i.test(description)) return null;

  return buildReviewItem({
    description,
    quantity,
    totalCost,
    unitCost: totalCost / quantity,
    confidence: Math.min(Number(confidence || 0), 70),
    reason: 'Lectura OCR local: confirma nombre, cantidad y valor.',
    sourceLine: index + 1,
  });
};

const parseInvoiceText = (text, confidence) => {
  const lines = String(text || '').split(/\r?\n/).map(normalizeSpaces).filter(Boolean);
  const hasUnitCostColumn = lines.some(line =>
    /(preciounitario|valorunitario|vrunitario|vrunit|costounitario|unitprice)/.test(compactText(line))
  );
  const invoiceLine = lines.find(line => /factura|venta/i.test(line)) || '';
  const invoiceNumber = (invoiceLine.match(/\b\d{3,10}\b/) || lines.join(' ').match(/\b(?:factura|venta)\D{0,15}(\d{3,10})/i) || [])[1]
    || (invoiceLine.match(/\b\d{3,10}\b/) || [])[0]
    || '';
  const totalLine = [...lines].reverse().find(line => /^(total|subtotal)/.test(compactText(line))) || '';
  const totalMatches = totalLine.match(/\d[\d.,]{2,}/g) || [];
  const total = totalMatches.length ? parseCOP(totalMatches[totalMatches.length - 1]) : 0;
  const items = lines
    .map((line, index) => parseItemLine(line, index, confidence, { hasUnitCostColumn }))
    .filter(Boolean);
  const calculatedTotal = items.reduce((sum, item) => sum + item.totalCost, 0);

  return {
    supplier: '',
    invoiceNumber,
    date: parseDate(lines.join(' ')),
    currency: 'COP',
    total: total || calculatedTotal,
    overallConfidence: Math.min(Number(confidence || 0) / 100, 0.7),
    notes: [
      'Procesado localmente sin servicios de pago.',
      'La escritura manuscrita siempre debe verificarse antes de registrar.',
    ],
    items,
    rawText: lines.join('\n'),
  };
};

const candidateScore = (items = [], declaredTotal = 0) => {
  const validItems = items.filter(item => Number(item?.totalCost) > 0 && Number(item?.quantity) > 0);
  if (!validItems.length) return Number.NEGATIVE_INFINITY;

  const sum = validItems.reduce((total, item) => total + Number(item.totalCost || 0), 0);
  const coherentRows = validItems.filter(item => {
    const expected = Number(item.quantity || 0) * Number(item.unitCost || 0);
    return expected > 0 && Math.abs(expected - Number(item.totalCost || 0)) / Number(item.totalCost || 1) <= 0.03;
  }).length;
  const coherence = coherentRows / validItems.length;
  const totalCloseness = declaredTotal > 0
    ? Math.max(0, 1 - (Math.abs(sum - declaredTotal) / declaredTotal))
    : 0;

  return validItems.length + (coherence * 10) + (totalCloseness * 30);
};

const selectBestItems = (candidates, declaredTotal) => (candidates || [])
  .filter(candidate => Array.isArray(candidate) && candidate.length)
  .sort((left, right) => candidateScore(right, declaredTotal) - candidateScore(left, declaredTotal))[0] || [];

const buildLayoutRows = (blocks = []) => {
  const words = (blocks || []).flatMap(block =>
    (block?.paragraphs || []).flatMap(paragraph =>
      (paragraph?.lines || []).flatMap(line => line?.words || [])
    )
  ).filter(word => word?.text && word?.bbox);
  if (!words.length) return [];

  const heights = words.map(word => Math.max(1, word.bbox.y1 - word.bbox.y0)).sort((a, b) => a - b);
  const medianHeight = heights[Math.floor(heights.length / 2)] || 20;
  const tolerance = Math.max(7, medianHeight * 0.65);
  const rows = [];

  words
    .map(word => ({ ...word, centerY: (word.bbox.y0 + word.bbox.y1) / 2 }))
    .sort((a, b) => a.centerY - b.centerY || a.bbox.x0 - b.bbox.x0)
    .forEach(word => {
      let row = rows.find(candidate => Math.abs(candidate.centerY - word.centerY) <= tolerance);
      if (!row) {
        row = { centerY: word.centerY, words: [] };
        rows.push(row);
      }
      row.words.push(word);
      row.centerY = row.words.reduce((sum, item) => sum + item.centerY, 0) / row.words.length;
    });

  return rows
    .map(row => {
      const sortedWords = row.words.sort((a, b) => a.bbox.x0 - b.bbox.x0);
      return {
        ...row,
        words: sortedWords,
        text: normalizeSpaces(sortedWords.map(word => word.text).join(' ')),
      };
    })
    .sort((a, b) => a.centerY - b.centerY);
};

const findHeaderWord = (row, pattern) => row?.words?.find(word => pattern.test(compactText(word.text)));

const parseLayoutItems = (rows, fallbackConfidence) => {
  const headerIndex = rows.findIndex(row => {
    const compact = compactText(row.text);
    return /(descripcion|producto|detalle|articulo|concepto)/.test(compact) && /(cant|cantidad)/.test(compact);
  });
  if (headerIndex < 0) return [];

  const header = rows[headerIndex];
  const descriptionHeader = findHeaderWord(header, /(descripcion|producto|detalle|articulo|concepto)/);
  const quantityHeader = findHeaderWord(header, /^(cant|cantidad|qty)$/);
  const unitCostHeader = findHeaderWord(header, /(prec|unitario|vrunit|valorunit)/);
  const totalHeaders = header.words.filter(word => /total/.test(compactText(word.text)));
  const totalHeader = totalHeaders[totalHeaders.length - 1];
  if (!descriptionHeader || !totalHeader || totalHeader.bbox.x0 <= descriptionHeader.bbox.x0) return [];

  const descriptionStart = descriptionHeader.bbox.x0;
  const wordCenter = word => (word.bbox.x0 + word.bbox.x1) / 2;
  const descriptionEnd = quantityHeader ? quantityHeader.bbox.x0 : totalHeader.bbox.x0;
  const quantityEnd = quantityHeader && unitCostHeader
    ? unitCostHeader.bbox.x0
    : totalHeader.bbox.x0;
  const totalStart = totalHeader.bbox.x0;
  const parsed = [];

  for (const row of rows.slice(headerIndex + 1)) {
    if (/^(total|subtotal)/.test(compactText(row.text))) break;
    if (ignoredLine(row.text)) continue;

    const quantityText = row.words
      .filter(word => {
        const center = wordCenter(word);
        return quantityHeader
          ? center >= descriptionEnd && center < quantityEnd
          : center < descriptionStart;
      })
      .map(word => word.text).join(' ');
    const description = normalizeSpaces(row.words
      .filter(word => {
        const center = wordCenter(word);
        return center >= descriptionStart && center < descriptionEnd;
      })
      .map(word => word.text).join(' '));
    const unitCostText = row.words
      .filter(word => {
        const center = wordCenter(word);
        return unitCostHeader && center >= quantityEnd && center < totalStart;
      })
      .map(word => word.text).join(' ');
    const totalText = row.words
      .filter(word => wordCenter(word) >= totalStart)
      .map(word => word.text).join(' ');
    const quantityMatch = quantityText.match(/\d+(?:[.,]\d+)?/);
    const readQuantity = quantityMatch ? Number(quantityMatch[0].replace(',', '.')) : 0;
    const unitCost = parseCOP(unitCostText);
    const totalCost = parseCOP(totalText);
    const inferredQuantity = unitCost > 0 ? totalCost / unitCost : 0;
    const inferredRounded = Math.round(inferredQuantity);
    const canInferQuantity = inferredRounded > 0
      && Math.abs(inferredQuantity - inferredRounded) <= 0.02;
    const quantity = readQuantity > 0 ? readQuantity : (canInferQuantity ? inferredRounded : 1);
    if (!description || !/[a-záéíóúñ]/i.test(description) || totalCost < 100) continue;

    const wordConfidence = row.words.length
      ? row.words.reduce((sum, word) => sum + Number(word.confidence || 0), 0) / row.words.length
      : fallbackConfidence;
    parsed.push(buildReviewItem({
      description,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      totalCost,
      unitCost: unitCost || (totalCost / (Number.isFinite(quantity) && quantity > 0 ? quantity : 1)),
      confidence: wordConfidence,
      reason: 'Fila reconstruida por posición: confirma nombre, cantidad y valor.',
    }));
  }
  return parsed;
};

export async function analyzeInvoiceLocally(files, onProgress = () => {}) {
  const worker = await createWorker(['spa', 'eng'], OEM.LSTM_ONLY, {
    logger: message => {
      if (message?.status) onProgress(message.status, Number(message.progress || 0));
    },
  });

  try {
    await worker.setParameters({
      tessedit_pageseg_mode: PSM.AUTO,
      preserve_interword_spaces: '1',
    });
    const pages = [];
    const pageItems = [];
    const confidences = [];
    for (let index = 0; index < files.length; index += 1) {
      onProgress(`Procesando página ${index + 1} de ${files.length}`, index / files.length);
      // Tesseract conserva mejor las tablas cuando recibe el archivo original.
      // Reescalar en canvas desplazaba Cant/Prec.Unit/Total y convertía el ítem en cantidad.
      const result = await worker.recognize(files[index], { rotateAuto: true }, { blocks: true });
      pages.push(result?.data?.text || '');
      pageItems.push(...parseLayoutItems(buildLayoutRows(result?.data?.blocks), result?.data?.confidence));
      confidences.push(Number(result?.data?.confidence || 0));
    }
    const averageConfidence = confidences.length
      ? confidences.reduce((sum, value) => sum + value, 0) / confidences.length
      : 0;
    onProgress('Lectura terminada', 1);
    const parsed = parseInvoiceText(pages.join('\n'), averageConfidence);
    parsed.items = selectBestItems([parsed.items, pageItems], parsed.total);
    if (!parsed.total) parsed.total = parsed.items.reduce((sum, item) => sum + item.totalCost, 0);
    return parsed;
  } finally {
    await worker.terminate();
  }
}

export { candidateScore, parseInvoiceText, selectBestItems };
